/**
 * Parent-side postMessage bridge. Validates messages from iframe using
 * triple validation: event.source identity, session nonce, and source field.
 * Routes iframe events to the event bus and sends commands to the iframe.
 */

import { eventBus } from '../core/events.js';
import { sanitize } from '../core/sanitizer.js';
import { store } from '../core/state.js';
import { extractSectionCssBackgroundImages } from '../shared/css-background-images.js';
import { getActiveViewportName } from '../ui/viewport-controls.js';
import { SPACING_PRESETS } from '../shared/spacing-presets.js';

/**
 * Close gaps between adjacent section positions so outlines are contiguous.
 * Pure function — returns a new array, does not mutate input.
 * Applies to non-shared page sections only (shared nav/footer are left intact).
 */
function _closePositionGaps(positions) {
  if (!positions || positions.length < 2) return positions;

  const MIN_HEIGHT = 20;

  const out = positions.map((p) => ({ ...p }));
  const pageIndexes = [];
  for (let i = 0; i < out.length; i++) {
    if (!out[i]?.shared) pageIndexes.push(i);
  }
  if (pageIndexes.length < 2) return out;

  for (let i = 1; i < pageIndexes.length; i++) {
    const prev = out[pageIndexes[i - 1]];
    const next = out[pageIndexes[i]];
    const prevBottom = prev.top + prev.height;
    const gap = next.top - prevBottom;

    if (Math.abs(gap) < 1) continue;

    if (gap > 0) {
      const half = gap / 2;
      prev.height += half;
      next.height += half;
      next.top -= half;
    } else if (gap < 0) {
      const shift = prevBottom - next.top;
      next.top = prevBottom;
      next.height = Math.max(MIN_HEIGHT, next.height - shift);
    }
  }

  return out;
}

export class Bridge {
  constructor() {
    this._iframe = null;
    this._nonce = null;
    this._handler = this._onMessage.bind(this);
    this._listening = false;
    this._lastScroll = { y: 0, zoom: 1 };
    this._lastScrollY = 0;
    this._currentZoom = 1;
    this._sectionSpacing = {};
    this._spacingGeneration = 0;
    this._spacingSequence = 0;
    this._spacingReply = null;
    // StyleSnapshot request/response bookkeeping. Monotonic id per request; the
    // latest id issued per `${instanceId}::${eid}` lets us drop stale responses
    // (e.g. a slow theme-change snapshot arriving after a newer click snapshot).
    this._styleSnapshotSeq = 0;
    this._latestStyleSnapshotReq = {};
  }

  attach(iframe, nonce) {
    // Reattaching swaps the rendered document, not the interaction mode. The
    // comment/client controller owns interceptor teardown explicitly; losing
    // it here makes the first render silently disable that controller.
    const clickInterceptor = this._clickInterceptor;
    this.detach();
    this._clickInterceptor = clickInterceptor;
    this._iframe = iframe;
    this._nonce = nonce;
    window.addEventListener('message', this._handler);
    this._listening = true;
  }

  detach() {
    this.invalidateSectionSpacing();
    if (this._listening) {
      window.removeEventListener('message', this._handler);
      this._listening = false;
    }
    this._iframe = null;
    this._nonce = null;
    this._clickInterceptor = null;
  }

  /**
   * Install (fn) or clear (null) the comment-mode click interceptor. The
   * function receives (type, payload) for element/section/canvas clicks and
   * returns true to CONSUME the click before any `iframe:*` re-emit.
   */
  setClickInterceptor(fn) {
    this._clickInterceptor = typeof fn === 'function' ? fn : null;
  }

  send(type, data) {
    if (!this._iframe || !this._iframe.contentWindow) {
      console.warn('[Bridge] No iframe attached, cannot send:', type);
      return;
    }
    if (type === 'set-zoom') {
      const nextZoom = Number(data?.zoom);
      this._currentZoom = Number.isFinite(nextZoom) && nextZoom > 0 ? nextZoom : 1;
    }
    this._iframe.contentWindow.postMessage(
      { source: 'pb-parent', nonce: this._nonce, type, data },
      '*'
    );
  }

  /**
   * Request a StyleSnapshot for one element. The iframe re-finds it by
   * instanceId+eid, resolves its own computed style + token identity, and
   * replies with `style-snapshot` (re-emitted as `iframe:style-snapshot`, stale
   * responses dropped). `candidateTokens` optionally overrides the iframe's
   * baked-in priority list. Eid-less form (instanceId only) snapshots the
   * section's first element child (section-panel background readback). Returns
   * the issued requestId (or null when neither instanceId nor eid is given).
   */
  requestStyleSnapshot({
    instanceId = '',
    eid = '',
    reason = 'click',
    candidateTokens = null,
    resolveValues = null
  } = {}) {
    if (!eid && !instanceId) return null;
    const requestId = (this._styleSnapshotSeq += 1);
    this._latestStyleSnapshotReq[`${instanceId}::${eid}`] = requestId;
    this.send('query-style-snapshot', {
      instanceId,
      eid,
      reason,
      requestId,
      candidateTokens,
      resolveValues
    });
    return requestId;
  }

  _normalizeScroll(data) {
    const rawY = Number(data?.y ?? data?.scrollY);
    const y = Number.isFinite(rawY) ? rawY : 0;
    const zoom = this._getZoom(data);
    return { y, zoom };
  }

  _normalizeSelectionTarget(data) {
    if (!data || typeof data !== 'object') return data;
    if (data.editableType !== 'css-background') {
      // Keep the parent-side contract complete even when an older iframe (or a
      // synthetic integration fixture) sends the pre-TargetRef DOM shape. The
      // eid remains a DOM eid; CSS targets are handled by the separate branch
      // below and can never be manufactured from an index.
      if (!data.targetRef && data.eid) {
        return { ...data, targetRef: { domain: 'dom', eid: data.eid } };
      }
      return data;
    }
    const state = store.getState?.() || {};
    const sections = [
      state.sharedSections?.navigation,
      state.sharedSections?.footer,
      ...(state.pages || []).flatMap((page) => page?.sections || [])
    ].filter(Boolean);
    const section = sections.find((entry) => entry.instanceId === data.instanceId);
    const fact = section
      ? extractSectionCssBackgroundImages(section.css || '', section.sectionId || '').find(
          (entry) => entry.index === data.cssBgIndex
        )
      : null;
    if (!fact) {
      return {
        ...data,
        kind: 'background',
        provenance: {
          domain: 'section-css',
          writable: false,
          reason: 'The winning background source could not be resolved to section CSS'
        }
      };
    }
    return {
      ...data,
      kind: 'background',
      cssBgIndex: fact.index,
      targetRef: {
        domain: 'section-css',
        key: fact.key,
        locator: fact.locator,
        expectedFingerprint: fact.expectedFingerprint
      },
      provenance: fact.provenance
    };
  }

  _getZoom(data) {
    const z = Number(data?.zoom);
    return Number.isFinite(z) && z > 0 ? z : 1;
  }

  /** Collab Phase 10: current preview zoom, for scaling peer cursors at render. */
  getZoom() {
    return this._currentZoom || 1;
  }

  /**
   * Scroll the canvas to a document offset expressed in VISUAL px — the space
   * every parent consumer already works in, because inbound geometry is
   * multiplied by zoom on the way out of `_normalizeSectionPositions`.
   *
   * The wire message takes RAW iframe px, so the conversion lives here rather
   * than in each caller: a rail, a minimap and a stepper getting this wrong
   * independently is exactly the bug class B0 was.
   *
   * @param {number} visualY  offset in zoom-scaled px
   * @param {{smooth?: boolean}} [opts]
   */
  scrollToVisualY(visualY, { smooth = true } = {}) {
    if (!Number.isFinite(visualY)) return;
    const zoom = this.getZoom() || 1;
    this.send('scroll-to-document-y', { y: visualY / zoom, smooth });
  }

  /**
   * EA-10 — one-shot scoped entrance preview (contracts §5). The wire carries
   * VALIDATED IDS, never a selector: the iframe builds the exact selector
   * itself — page scope (null, null), one section, or one element.
   */
  previewEntrance({ instanceId = null, eid = null } = {}) {
    this.send('preview-entrance', { instanceId, eid });
  }

  _scaleNumber(value, zoom) {
    return Number.isFinite(value) ? value * zoom : value;
  }

  _scaleRect(rect, zoom) {
    if (!rect || typeof rect !== 'object') return rect;
    return {
      ...rect,
      top: this._scaleNumber(rect.top, zoom),
      left: this._scaleNumber(rect.left, zoom),
      width: this._scaleNumber(rect.width, zoom),
      height: this._scaleNumber(rect.height, zoom)
    };
  }

  _scaleLogoRect(rect, zoom) {
    if (!rect || typeof rect !== 'object') return rect;
    return {
      ...rect,
      x: this._scaleNumber(rect.x, zoom),
      y: this._scaleNumber(rect.y, zoom),
      width: this._scaleNumber(rect.width, zoom),
      height: this._scaleNumber(rect.height, zoom)
    };
  }

  _scaleLogoGeometry(data, zoom) {
    return {
      logoSlotRect: data.logoSlotRect
        ? this._scaleLogoRect(data.logoSlotRect, zoom)
        : data.logoSlotRect,
      logoBoundaryRect: data.logoBoundaryRect
        ? this._scaleLogoRect(data.logoBoundaryRect, zoom)
        : data.logoBoundaryRect,
      logoBlockerRects: Array.isArray(data.logoBlockerRects)
        ? data.logoBlockerRects.map((rect) => this._scaleLogoRect(rect, zoom))
        : data.logoBlockerRects
    };
  }

  _normalizeSectionPositions(data) {
    if (!data || typeof data !== 'object') return data;
    const zoom = this._getZoom(data);

    const scaledPositions = Array.isArray(data.positions)
      ? zoom === 1
        ? data.positions.map((pos) => ({ ...pos }))
        : data.positions.map((pos) => ({
            ...pos,
            top: this._scaleNumber(pos?.top, zoom),
            height: this._scaleNumber(pos?.height, zoom)
          }))
      : data.positions;

    return {
      ...data,
      positions: _closePositionGaps(scaledPositions),
      contentHeight: zoom === 1 ? data.contentHeight : this._scaleNumber(data.contentHeight, zoom),
      // Scaled beside contentHeight so a viewport band stays in the same space
      // as the section tops it is drawn against.
      viewportHeight:
        zoom === 1 ? data.viewportHeight : this._scaleNumber(data.viewportHeight, zoom),
      scrollY: zoom === 1 ? data.scrollY : this._scaleNumber(data.scrollY, zoom),
      zoom
    };
  }

  _scaleContainer(data, zoom) {
    return {
      containerWidth:
        data.containerWidth != null ? this._scaleNumber(data.containerWidth, zoom) : null,
      containerLeft: data.containerLeft != null ? this._scaleNumber(data.containerLeft, zoom) : null
    };
  }

  _normalizeElementGeometry(type, data) {
    if (!data || typeof data !== 'object') return data;
    const zoom = this._getZoom(data);
    if (zoom === 1) return data;

    if (type === 'element-hovered') {
      return {
        ...data,
        rect: this._scaleRect(data.rect, zoom),
        ...this._scaleContainer(data, zoom),
        pictureRect: data.pictureRect ? this._scaleRect(data.pictureRect, zoom) : null,
        childRects: Array.isArray(data.childRects)
          ? data.childRects.map((rect) => this._scaleRect(rect, zoom))
          : data.childRects,
        zoom
      };
    }

    return {
      ...data,
      rect: this._scaleRect(data.rect, zoom),
      ...this._scaleContainer(data, zoom),
      pictureRect: data.pictureRect ? this._scaleRect(data.pictureRect, zoom) : null,
      ...this._scaleLogoGeometry(data, zoom),
      zoom
    };
  }

  _normalizeSpacingGroup(data) {
    if (!data || typeof data !== 'object') return data;
    const zoom = this._getZoom(data);
    if (zoom === 1) return data;
    return {
      ...data,
      items: Array.isArray(data.items)
        ? data.items.map((item) => ({
            ...item,
            rect: this._scaleRect(item?.rect, zoom)
          }))
        : data.items,
      zoom
    };
  }

  _normalizeTextSelection(data) {
    if (!data || typeof data !== 'object') return data;
    const zoom = this._getZoom(data);
    if (zoom === 1) return data;
    return {
      ...data,
      x: this._scaleNumber(data.x, zoom),
      y: this._scaleNumber(data.y, zoom),
      width: this._scaleNumber(data.width, zoom),
      height: this._scaleNumber(data.height, zoom),
      zoom
    };
  }

  _normalizeBindMenuRequest(data) {
    if (!data || typeof data !== 'object') return data;
    const zoom = this._getZoom(data);
    if (zoom === 1) return data;
    return {
      ...data,
      rect: this._scaleRect(data.rect, zoom),
      point: data.point
        ? {
            top: this._scaleNumber(data.point.top, zoom),
            left: this._scaleNumber(data.point.left, zoom)
          }
        : data.point,
      zoom
    };
  }

  _normalizeReorderPayload(data) {
    if (!data || typeof data !== 'object') return data;
    const zoom = this._getZoom(data);
    if (zoom === 1) return data;
    return {
      ...data,
      rect: data.rect ? this._scaleRect(data.rect, zoom) : data.rect,
      indicatorRect: data.indicatorRect
        ? this._scaleRect(data.indicatorRect, zoom)
        : data.indicatorRect,
      zoom
    };
  }

  _onMessage(event) {
    if (!this._iframe) return;
    if (event.source !== this._iframe.contentWindow) return;
    const msg = event.data;
    if (!msg || msg.source !== 'pb-iframe') return;
    if (msg.nonce !== this._nonce) return;

    // Store scroll position reports without emitting to event bus
    if (msg.type === 'scroll-position') {
      const next = this._normalizeScroll(msg.data);
      this._lastScroll = next;
      this._lastScrollY = next.y;
      this._currentZoom = next.zoom;
      return;
    }

    // Glass overlay: section positions (high-frequency, rAF-throttled)
    if (msg.type === 'section-positions') {
      const next = this._normalizeScroll(msg.data);
      this._lastScroll = next;
      this._lastScrollY = next.y;
      this._currentZoom = next.zoom;
      eventBus.emit('iframe:section-positions', this._normalizeSectionPositions(msg.data));
      return;
    }

    // Collab Phase 10: the LOCAL user's live pointer (document-relative, iframe
    // CSS px — zoom/scroll-independent so it travels the wire unscaled; the
    // receiving peer scales by ITS own zoom at render). Null on pointerleave.
    if (msg.type === 'collab-pointer') {
      const d = msg.data;
      const point = d && Number.isFinite(d.x) && Number.isFinite(d.y) ? { x: d.x, y: d.y } : null;
      eventBus.emit('iframe:collab-pointer', point);
      return;
    }

    // Section hover — single source of truth for overlay + section list
    if (msg.type === 'section-hovered') {
      eventBus.emit('section:hover', { ...(msg.data || {}), fromIframe: true });
      return;
    }

    if (msg.type === 'section-clicked') {
      eventBus.emit('iframe:section-clicked', { ...(msg.data || {}), fromIframe: true });
      return;
    }

    if (msg.type === 'section-double-clicked') {
      const payload = { ...(msg.data || {}), fromIframe: true };
      eventBus.emit('iframe:section-clicked', payload);
      eventBus.emit('iframe:section-double-clicked', payload);
      return;
    }

    // Store section spacing response for direct retrieval
    if (msg.type === 'section-spacing-response') {
      const reply = msg.data;
      if (
        !reply ||
        reply.requestId !== this._spacingSequence ||
        reply.generation !== this._spacingGeneration ||
        reply.stateEpoch !== store.getEpoch() ||
        reply.viewport?.name !== getActiveViewportName() ||
        !Number.isFinite(reply.viewport?.width) ||
        reply.viewport.width <= 0 ||
        !Number.isFinite(reply.rootPx) ||
        reply.rootPx <= 0
      )
        return;
      this._sectionSpacing = reply.values || {};
      this._spacingReply = reply;
      eventBus.emit('iframe:section-spacing-response', reply);
      return;
    }

    // Normalize geometry payloads to visual coordinates when iframe zoom is active
    if (
      msg.type === 'element-rect' ||
      msg.type === 'element-rect-update' ||
      msg.type === 'element-hovered'
    ) {
      eventBus.emit(
        `iframe:${msg.type}`,
        this._normalizeElementGeometry(msg.type, this._normalizeSelectionTarget(msg.data))
      );
      return;
    }

    if (msg.type === 'spacing-group-result') {
      eventBus.emit('iframe:spacing-group-result', this._normalizeSpacingGroup(msg.data));
      return;
    }

    if (msg.type === 'text-selected') {
      eventBus.emit('iframe:text-selected', this._normalizeTextSelection(msg.data));
      return;
    }

    if (msg.type === 'request-bind-menu') {
      eventBus.emit('iframe:request-bind-menu', this._normalizeBindMenuRequest(msg.data));
      return;
    }

    if (msg.type === 'request-slash-menu') {
      eventBus.emit('iframe:request-slash-menu', this._normalizeBindMenuRequest(msg.data));
      return;
    }

    if (msg.type === 'request-element-menu') {
      eventBus.emit('iframe:request-element-menu', this._normalizeBindMenuRequest(msg.data));
      return;
    }

    if (msg.type === 'request-decorative-menu') {
      eventBus.emit('iframe:request-decorative-menu', this._normalizeBindMenuRequest(msg.data));
      return;
    }

    if (msg.type === 'request-section-menu') {
      eventBus.emit('iframe:request-section-menu', this._normalizeBindMenuRequest(msg.data));
      return;
    }

    if (msg.type === 'reorder-drag-begin') {
      eventBus.emit('iframe:reorder-drag-begin', this._normalizeReorderPayload(msg.data));
      return;
    }

    if (msg.type === 'reorder-commit' || msg.type === 'reorder-drag-cancel') {
      eventBus.emit(`iframe:${msg.type}`, msg.data);
      return;
    }

    if (msg.type === 'element-move-request') {
      eventBus.emit('element:move-request', msg.data);
      return;
    }

    // StyleSnapshot response: drop stale replies (older than the latest request
    // issued for this element) before panels consume them.
    if (msg.type === 'style-snapshot') {
      const d = msg.data || {};
      const latest = this._latestStyleSnapshotReq[`${d.instanceId || ''}::${d.eid || ''}`];
      if (latest != null && d.requestId != null && d.requestId < latest) return;
      eventBus.emit('iframe:style-snapshot', d);
      return;
    }

    // Comment-mode click interception (UX Pack F5b): ONE choke point at the
    // single generic re-emit. A truthy interceptor return CONSUMES the click
    // — element-selection, the text editor, and every other edit consumer see
    // nothing. Installed/uninstalled by the comment controller; mode-off and
    // destroy always clear it (teardown is the installer's contract).
    if (
      this._clickInterceptor &&
      (msg.type === 'element-clicked' ||
        msg.type === 'section-clicked' ||
        msg.type === 'canvas-clicked')
    ) {
      let consumed = false;
      try {
        consumed =
          this._clickInterceptor(msg.type, this._normalizeSelectionTarget(msg.data)) === true;
      } catch {
        /* a broken interceptor must not eat the canvas — treat as unconsumed */
      }
      if (consumed) return;
    }

    const payload = this._normalizeSelectionTarget(msg.data);
    if (msg.type === 'content-changed' && payload && typeof payload.html === 'string') {
      // Keep the live DOM payload available to the text editor so it can replace
      // pre-existing media/form descendants with trusted preservation tokens
      // before sanitizing. All ordinary consumers continue to see sanitized HTML.
      payload.rawHtml = payload.html;
      payload.html = sanitize(payload.html);
    }
    eventBus.emit(`iframe:${msg.type}`, payload);
  }

  get nonce() {
    return this._nonce;
  }

  get lastScroll() {
    const y = Number.isFinite(this._lastScrollY)
      ? this._lastScrollY
      : Number.isFinite(this._lastScroll?.y)
        ? this._lastScroll.y
        : 0;
    const zoom =
      Number.isFinite(this._lastScroll?.zoom) && this._lastScroll.zoom > 0
        ? this._lastScroll.zoom
        : 1;
    return { y, zoom };
  }

  get lastScrollY() {
    return Number.isFinite(this._lastScrollY) ? this._lastScrollY : 0;
  }

  get currentZoom() {
    return Number.isFinite(this._currentZoom) && this._currentZoom > 0 ? this._currentZoom : 1;
  }

  clearScroll() {
    this._lastScroll = { y: 0, zoom: this.currentZoom };
    this._lastScrollY = 0;
  }

  invalidateSectionSpacing() {
    this._spacingGeneration += 1;
    this._sectionSpacing = {};
    this._spacingReply = null;
  }

  querySectionSpacing() {
    const request = {
      requestId: ++this._spacingSequence,
      generation: this._spacingGeneration,
      stateEpoch: store.getEpoch(),
      viewportName: getActiveViewportName(),
      presetValues: SPACING_PRESETS.map((preset) => preset.value)
    };
    this.send('query-section-spacing', request);
    return request;
  }

  /** In-flight style replies still name the pre-repair occurrence. */
  invalidateEditableIdentity(instanceIds) {
    const affected = new Set(instanceIds);
    const boundary = ++this._styleSnapshotSeq;
    for (const key of Object.keys(this._latestStyleSnapshotReq)) {
      if (affected.has(key.split('::')[0])) this._latestStyleSnapshotReq[key] = boundary;
    }
    this.invalidateSectionSpacing();
  }

  getSectionSpacing(instanceId) {
    const reply = this._spacingReply;
    if (
      !reply ||
      reply.requestId !== this._spacingSequence ||
      reply.generation !== this._spacingGeneration ||
      reply.stateEpoch !== store.getEpoch() ||
      reply.viewport?.name !== getActiveViewportName()
    )
      return null;
    const values = this._sectionSpacing[instanceId];
    return values
      ? {
          values,
          generation: reply.generation,
          requestId: reply.requestId,
          viewport: reply.viewport,
          rootPx: reply.rootPx,
          presets: reply.presets || {}
        }
      : null;
  }
}

export const bridge = new Bridge();
